import React from 'react';

import { Typography } from '@mui/material';
import { Check } from 'lucide-react';
import { createPortal } from 'react-dom';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import { SkillFileIcon } from './skill-file-icon';

const useStyles = makeStyles()(theme => ({
  actions: { alignItems: 'center', display: 'flex', flexShrink: 0, gap: 4 },
  // Stays at the top of the editor's scroll area while a long file scrolls under it.
  root: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexShrink: 0,
    gap: 16,
    justifyContent: 'space-between',
    minHeight: 56,
    padding: '0 28px',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  // Lays what an editor renders here out in the actions row, and takes no gap while empty.
  slot: { display: 'contents' },
  status: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 12,
    gap: theme.spacing(0.5),
  },
  statusSaved: {
    color: theme.palette.success.main,
  },
  /**
   * Wide enough for both "Saving..." and "✓ Saved", and right-aligned, so switching between
   * them on every autosave does not move the file actions to its left.
   */
  statusText: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(0.5),
    justifyContent: 'flex-end',
    minWidth: 64,
    whiteSpace: 'nowrap',
  },
  title: { alignItems: 'center', display: 'flex', flex: 1, gap: 12, minWidth: 0 },
  titleText: { fontSize: 16, fontWeight: 600 },
}));

const SkillSaveStatus = React.memo(function SkillSaveStatus({
  runHint,
  statusLabel,
}: {
  runHint: string;
  statusLabel: string;
}) {
  const { classes, cx } = useStyles();
  const saved = statusLabel === 'Saved';
  return (
    <span aria-live="polite" className={classes.status}>
      {Boolean(runHint) && (
        <>
          <AlineTooltip arrow placement="top" title={runHint}>
            <span>Draft</span>
          </AlineTooltip>
          <span aria-hidden="true">·</span>
        </>
      )}
      <span className={cx(classes.statusText, saved && classes.statusSaved)}>
        {saved && <Check aria-hidden="true" size={14} />}
        {statusLabel}
      </span>
    </span>
  );
});

/**
 * The open file's name, the save status, and two slots the file's editor renders its own
 * controls into.
 */
export const SkillFileHeader = React.memo(function SkillFileHeader({
  contentType,
  name,
  runHint,
  setActionsContainer,
  setPreviewControlsContainer,
  statusLabel,
}: {
  /** What the open file is, for its icon. */
  contentType: string;
  name: string;
  /** Why the skill cannot run yet, shown behind a Draft label, or ''. */
  runHint: string;
  setActionsContainer: (node: HTMLDivElement | null) => void;
  setPreviewControlsContainer: (node: HTMLDivElement | null) => void;
  /** `null` for a file that is only previewed, such as a PDF: it has nothing to save. */
  statusLabel: string | null;
}) {
  const { classes } = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <SkillFileIcon contentType={contentType} />
        <Typography noWrap className={classes.titleText} component="h2">
          {name}
        </Typography>
      </div>
      <div className={classes.actions}>
        <div ref={setPreviewControlsContainer} className={classes.slot} />
        <div ref={setActionsContainer} className={classes.slot} />
        {statusLabel !== null && <SkillSaveStatus runHint={runHint} statusLabel={statusLabel} />}
      </div>
    </div>
  );
});

/**
 * Renders `children` into one of the header's slots. `undefined` renders them in place, for an
 * editor shown without the header; `null` is a slot not mounted yet, so nothing shows.
 */
export const SkillHeaderSlot = React.memo(function SkillHeaderSlot({
  children,
  container,
}: {
  children: React.ReactNode;
  container: HTMLElement | null | undefined;
}) {
  if (container) return createPortal(children, container);
  if (container === undefined) return <>{children}</>;
  return null;
});
